// src/data/clientDemo.js
// Data-only. No components. No logic.
// This powers clickable weeks + consistent “Rakim arc” copy.

export const CLIENT_DEMO = {
  client: {
    id: "demo-alex",
    name: "Alex",
    initials: "AM",
    systemStatus: "Online",
  },

  ui: {
    // Short “bar” phrases (Rakim-style: repeatable, memorable)
    weekTaglines: {
      1: "See the patterns.",
      2: "Remove friction.",
      3: "One lever at a time.",
      4: "Lock it in.",
    },

    // Context line above coach note (changes per week)
    contextLines: {
      1: "Here’s what we’re learning about your baseline…",
      2: "Here’s what’s creating friction right now…",
      3: "Here’s what I’m seeing this week…",
      4: "Here’s what we’re keeping—so progress sticks…",
    },

    // CTA labels by “mode” logic (Global defaults, overridden per week)
    cta: {
      currentWeek: "Accept Adjustment",
      pastWeek: "View Past Plan",
      upcomingWeek: "Preview Next Week",
      logCheckIn: "Log Check-in",
    },
  },

  currentWeek: 3, // This drives the "Active" state logic

  // Timeline for the nav bar
  timeline: [
    { week: 1, label: "Awareness", status: "complete" },
    { week: 2, label: "Calibration", status: "complete" },
    { week: 3, label: "Optimization", status: "active" },
    { week: 4, label: "Consolidation", status: "upcoming" },
  ],

  // The actual content for each week
  weeks: {
    1: {
      week: 1,
      phase: "Awareness",
      cta: {
        mode: "PAST_SUMMARY",
        label: "View Week 1 Summary",
        helper: "What we measured + what we learned.",
      },
      // When viewing past Week 1, the "Next" step was Week 2
      preview: {
        enabled: true,
        label: "Preview Week 2",
        modal: {
          title: "Week 2 Preview — Remove Friction",
          subtitle: "We keep consistency. We reduce irritation.",
          why: "Now that baseline is clear, week 2 is about making training repeatable: less joint noise, less decision fatigue.",
          stays: [
            "2–3 sessions",
            "Technique-first lifts",
            "Pain stays quiet rule",
          ],
          changes: [
            "Warmup becomes structured",
            "Tempo work to reduce joint stress",
            "Recovery rules get specific",
          ],
          checklist: [
            "10-min mobility primer before sessions",
            "Pick 2 lifts to repeat all week",
            "2 early nights to stabilize recovery",
          ],
          coachLine: "Same engine. Less drag.",
        },
      },
      coachNote: {
        title: "Week 1 Baseline Read",
        summary:
          "We’re mapping movement quality, recovery capacity, and stress load. No intensity yet—just clean signals so we don’t build on a bad foundation.",
        posted: "Oct 10",
        duration: "3:08",
        videoThumbnail:
          "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=1000&q=80",
      },
      signals: [
        {
          label: "Movement Quality",
          status: "Developing",
          level: 6,
          color: "var(--brand)",
        },
        {
          label: "Recovery Capacity",
          status: "Unclear",
          level: 5,
          color: "#f59e0b",
        },
        {
          label: "Consistency",
          status: "Building",
          level: 6,
          color: "var(--brand)",
        },
      ],
      focus: {
        title: "Baseline Clarity",
        subtitle: "We don’t fix yet. We identify what’s true.",
      },
      plan: {
        adjustment:
          "Complete baseline check-in + movement screen. Keep sessions light.",
        tasks: [
          { title: "Movement Screen: 10-point audit", status: "done" },
          { title: "Lifestyle Review: sleep/stress", status: "done" },
          { title: "Training: 2 easy technique sessions", status: "done" },
        ],
      },
    },

    2: {
      week: 2,
      phase: "Calibration",
      cta: {
        mode: "PAST_SUMMARY",
        label: "View Week 2 Summary",
        helper: "What we simplified + what improved.",
      },
      preview: {
        enabled: true,
        label: "Preview Week 3",
        modal: {
          title: "Week 3 Preview — One Lever",
          subtitle: "We don’t add more. We adjust the limiter.",
          why: "Consistency is rising. Now we optimize around the bottleneck so you don’t stall or flare pain.",
          stays: ["Warmup structure", "Pain-aware ranges", "3 sessions"],
          changes: [
            "Volume becomes adjustable",
            "Sleep becomes a training variable",
            "We consolidate gains",
          ],
          checklist: [
            "Track bedtime consistency 3 nights",
            "Keep sessions crisp (no junk volume)",
            "Pick one lift to progress by 1%",
          ],
          coachLine: "One lever at a time.",
        },
      },
      coachNote: {
        title: "Week 2 Friction Removal",
        summary:
          "This week is about reducing joint stress and decision fatigue. We’re adjusting warmups, ranges, and recovery rules so consistency becomes automatic.",
        posted: "Oct 17",
        duration: "3:31",
        videoThumbnail:
          "https://images.unsplash.com/photo-1517838277536-f5f99be5017f?auto=format&fit=crop&w=1000&q=80",
      },
      signals: [
        {
          label: "Movement Quality",
          status: "Improving",
          level: 7,
          color: "var(--brand)",
        },
        {
          label: "Recovery Capacity",
          status: "Limited",
          level: 4,
          color: "#f59e0b",
        },
        {
          label: "Consistency",
          status: "Rising",
          level: 7,
          color: "var(--brand)",
        },
      ],
      focus: {
        title: "Movement Friction",
        subtitle: "Remove irritation now so you can train longer later.",
      },
      plan: {
        adjustment:
          "Reduce friction: adjust warmups ranges so pain stays quiet.",
        tasks: [
          { title: "Mobility: hip/ankle unlock", status: "done" },
          { title: "Strength: tempo work (lighter)", status: "done" },
          { title: "Recovery rule: 2 walks + 2 early nights", status: "done" },
        ],
      },
    },

    3: {
      week: 3,
      phase: "Optimization",
      cta: {
        mode: "ACCEPT",
        label: "Accept Adjustment",
        helper: "Updates your plan for the week.",
      },
      // DISABLED PREVIEW here so the button defaults to showing
      // the CURRENT week's adjustment details (The "Resolution").
      preview: {
        enabled: false,
      },
      coachNote: {
        title: "Week 3 Adjustment Logic",
        summary:
          "Movement looks solid, but sleep variability is capping recovery. Pulling volume back slightly to consolidate gains and keep progress moving.",
        posted: "2 hours ago",
        duration: "3:42",
        videoThumbnail:
          "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1000&q=80",
      },
      signals: [
        {
          label: "Movement Quality",
          status: "Strong",
          level: 9,
          color: "var(--brand)",
        },
        {
          label: "Recovery Capacity",
          status: "Limited",
          level: 4,
          color: "#f59e0b",
        },
        {
          label: "Consistency",
          status: "High",
          level: 9,
          color: "var(--brand)",
        },
      ],
      focus: {
        title: "Sleep Variability",
        subtitle: "Everything else is secondary this week.",
      },
      plan: {
        adjustment: "Reduce training volume (-10%) to match recovery.",
        tasks: [
          { title: "Session A: Lower Push", status: "done" },
          { title: "Mobility: Thoracic Flow", status: "pending" },
          { title: "Session B: Upper Pull", status: "locked" },
        ],
      },
    },

    4: {
      week: 4,
      phase: "Consolidation",
      cta: {
        mode: "PREVIEW",
        label: "Preview Next Cycle", // Points forward to Week 5+
        helper: "See what’s next before it becomes active.",
      },
      // The "Next Week" content for Week 4 is actually Cycle 2
      preview: {
        enabled: true,
        label: "Preview Cycle 2",
        modal: {
          title: "Cycle 2 Preview — Capacity",
          subtitle: "We built the floor. Now we raise the ceiling.",
          why: "Cycle 1 was about removing friction. Cycle 2 is about adding load. We keep the habits, but we increase the demand.",
          stays: ["Morning routine", "Protein targets", "Sleep guardrails"],
          changes: [
            "Hypertrophy focus",
            "Compound lifts heavier",
            "Conditioning added",
          ],
          checklist: [
            "Retest 5RM on main lifts",
            "Increase calories by 200/day",
            "Add Zone 2 cardio session",
          ],
          coachLine: "The system is ready. Let's drive it.",
        },
      },
      coachNote: {
        title: "Week 4 Standard Lock-In",
        summary:
          "We’re keeping what worked and simplifying what didn’t. The goal is repeatability: fewer moving parts, more consistency, and a stronger base for the next cycle.",
        posted: "Upcoming",
        duration: "3:12",
        videoThumbnail:
          "https://images.unsplash.com/photo-1517963879433-6ad2b056d712?auto=format&fit=crop&w=1000&q=80",
      },
      signals: [
        {
          label: "Movement Quality",
          status: "Strong",
          level: 8,
          color: "var(--brand)",
        },
        {
          label: "Recovery Capacity",
          status: "Improving",
          level: 6,
          color: "#f59e0b",
        },
        {
          label: "Consistency",
          status: "High",
          level: 8,
          color: "var(--brand)",
        },
      ],
      focus: {
        title: "Consistency Under Real Life",
        subtitle: "If it isn’t repeatable, it isn’t real.",
      },
      plan: {
        adjustment: "Lock in the standard: repeat the week that worked.",
        tasks: [
          { title: "Repeat Week 3 schedule", status: "locked" },
          { title: "Progress 1 lift only", status: "locked" },
          { title: "Recovery: deload cue if sleep dips", status: "locked" },
        ],
      },
    },
  },
};
